import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Radar, RefreshCw, Play, Pause, MapPin, Wifi, Globe, AlertTriangle, Download, FileText } from "lucide-react";
import { api, apiErr } from "./lib/api";
import { Card, Btn, Badge, PageHeader, Th, Td } from "./ui";

const KNOWN_ATTACKER = "103.62.238.98";

// "2026-09-20 17:24:49 +00:00" -> { d: "20-09-2026", t: "17:24:49" }
function fmtLogTime(v) {
  if (!v) return { d: "—", t: "" };
  const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return { d: String(v), t: "" };
  return { d: `${m[3]}-${m[2]}-${m[1]}`, t: `${m[4]}:${m[5]}:${m[6]}` };
}

function srcMeta(src) {
  if (src === "gotcha-listinvoice") return { tone: "rose", label: "TRAP · listinvoice" };
  if (src === "gotcha-adupload") return { tone: "rose", label: "TRAP · AD_upload" };
  if (src === "listinvoice") return { tone: "amber", label: "silent · listinvoice" };
  return { tone: "slate", label: src || "—" };
}

export function Watchlist() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [live, setLive] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (manual) => {
    setBusy(true);
    try {
      const { data } = await api.get("/security/trap-hits");
      setData(data);
      setErr("");
      setFetchedAt(new Date());
    } catch (e) {
      setErr(apiErr(e));
      if (manual) toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [exporting, setExporting] = useState("");
  const doExport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/security/trap-hits/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      a.href = url;
      a.download = `watchlist_${stamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setExporting("");
    }
  };

  useEffect(() => {
    if (live) {
      timer.current = setInterval(() => load(), 30000);
      return () => clearInterval(timer.current);
    }
  }, [live, load]);

  const hits = data?.hits || [];

  const stats = [
    { label: "Total hits", value: data?.total ?? "—", icon: Radar, tone: "text-rose-400" },
    { label: "Unique IPs", value: data?.unique_ips ?? "—", icon: Globe, tone: "text-sky-400" },
    { label: "Last hit", value: data?.last_hit ? fmtLogTime(data.last_hit).t : "—",
      sub: data?.last_hit ? fmtLogTime(data.last_hit).d : "", icon: Wifi, tone: "text-amber-400" },
  ];

  return (
    <div className="animate-fade-up" data-testid="watchlist-page">
      <PageHeader
        title="Attacker Watchlist"
        subtitle="Live hits on the legacy honeypot traps · IP, network, location & device"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Btn data-testid="watchlist-export-csv" variant="ghost" onClick={() => doExport("csv")} disabled={!!exporting}>
              <Download size={15} className={exporting === "csv" ? "animate-pulse" : ""} /> CSV
            </Btn>
            <Btn data-testid="watchlist-export-pdf" variant="ghost" onClick={() => doExport("pdf")} disabled={!!exporting}>
              <FileText size={15} className={exporting === "pdf" ? "animate-pulse" : ""} /> PDF
            </Btn>
            <Btn data-testid="watchlist-live-toggle" variant="ghost" onClick={() => setLive((v) => !v)}>
              {live ? <Pause size={15} /> : <Play size={15} />}{live ? "Live" : "Paused"}
            </Btn>
            <Btn data-testid="watchlist-refresh" onClick={() => load(true)} disabled={busy}>
              <RefreshCw size={15} className={busy ? "animate-spin" : ""} /> Refresh
            </Btn>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        {stats.map((s) => (
          <Card key={s.label} className="p-5" data-testid={`watchlist-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono uppercase tracking-wider text-slate-400">{s.label}</span>
              <s.icon className={s.tone} size={18} />
            </div>
            <div className={`mt-3 text-3xl font-mono font-bold tracking-tight ${s.tone}`}>{s.value}</div>
            {s.sub ? <div className="text-xs font-mono text-slate-500 mt-1">{s.sub}</div> : null}
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-4 text-xs font-mono text-slate-500">
        <span className={`w-2 h-2 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-slate-600"}`} />
        {live ? "Auto-refreshing every 30s" : "Auto-refresh paused"}
        {fetchedAt ? <span>· updated {fetchedAt.toLocaleTimeString()}</span> : null}
      </div>

      {err ? (
        <Card className="mt-6 p-5 border-rose-800/60" data-testid="watchlist-error">
          <div className="flex items-center gap-2 text-rose-300 text-sm"><AlertTriangle size={16} /> {err}</div>
        </Card>
      ) : null}

      <Card className="mt-6 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead className="bg-slate-900/60 border-b border-slate-800">
              <tr>
                <Th>Time (UTC)</Th>
                <Th>IP address</Th>
                <Th>ISP / Network</Th>
                <Th>Location</Th>
                <Th>WebRTC / Local IP</Th>
                <Th>Entry point</Th>
                <Th>Browser</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {hits.length === 0 && !busy ? (
                <tr><Td className="text-slate-500" colSpan={7}>No trap hits recorded yet.</Td></tr>
              ) : null}
              {hits.map((h, i) => {
                const t = fmtLogTime(h.time);
                const meta = srcMeta(h.source);
                const isAttacker = h.ip === KNOWN_ATTACKER;
                return (
                  <tr key={i} className={`hover:bg-slate-800/40 [transition:background-color_.15s] ${isAttacker ? "bg-rose-500/5" : ""}`} data-testid={`trap-hit-row-${i}`}>
                    <Td className="whitespace-nowrap font-mono text-xs">
                      <div className="text-slate-200">{t.t}</div>
                      <div className="text-slate-500">{t.d}</div>
                    </Td>
                    <Td className="whitespace-nowrap font-mono">
                      <span className={isAttacker ? "text-rose-300 font-bold" : "text-slate-100"}>{h.ip}</span>
                      {isAttacker ? <div className="text-[10px] text-rose-400 font-mono">known intruder</div> : null}
                      {h.xff && h.xff !== "-" ? <div className="text-[10px] text-slate-500">xff {h.xff}</div> : null}
                    </Td>
                    <Td className="text-xs text-slate-300 max-w-[220px]">{h.isp || "—"}</Td>
                    <Td className="text-xs text-slate-300 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-slate-500" />{h.location || "—"}</span>
                    </Td>
                    <Td className="font-mono text-xs text-sky-300">{h.webrtc || "—"}</Td>
                    <Td><Badge tone={meta.tone}>{meta.label}</Badge>
                      {h.uri ? <div className="text-[10px] font-mono text-slate-500 mt-1 max-w-[200px] truncate">{h.method} {h.uri}</div> : null}
                    </Td>
                    <Td className="text-[11px] text-slate-400 max-w-[240px] truncate" title={h.ua}>{h.ua}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
